import React from "react";

import { InfoTooltip, RefSelect, TagInput } from "../BuilderUtils";
import { GRAPH_COMPONENTS } from "../pageKinds";
import { scoreOptionsOf } from "./common";

/**
 * UNE LISTE DE COMPOSANTS GRAPHIQUES : celle d'une page `score`, ou celle d'une
 * section ajoutée à un bilan (BoardSectionsInspector.js). Même forme, même rendu
 * (GraphGrid.js), donc même formulaire.
 */
export function GraphListEditor({ list, onChange, scoreDic }) {
  const scoreOptions = scoreOptionsOf(scoreDic);
  const breakdownOptions = scoreOptions.filter((o) => {
    const s = scoreDic?.[o.value];
    return s?.metadata?.standard && s?.metadata?.current;
  });

  return (
    <>
      <div className="builder-section-header">
        <h3 className="builder-subsection-title">
          <span className="field-label-row">
            Composants graphiques ({list.length})
            <InfoTooltip text="Composants affichés, chacun dans sa carte, dans l'ordre de la liste." />
          </span>
        </h3>
        <button
          className="builder-btn-secondary"
          onClick={() => onChange([...list, { component_id: GRAPH_COMPONENTS[0].id, metadata: {} }])}
        >+ Ajouter</button>
      </div>

      {list.length === 0 && (
        <div className="builder-empty">Aucun composant graphique additionnel</div>
      )}

      {list.map((item, idx) => {
        const compInfo = GRAPH_COMPONENTS.find((c) => c.id === item.component_id);
        const setItem = (fields) => onChange(list.map((it, i) => (i === idx ? { ...it, ...fields } : it)));
        return (
          <div key={idx} className="builder-consequence-row">
            <select
              className="builder-input"
              value={item.component_id}
              // Changer de composant vide la metadata : elle décrivait le
              // réglage de l'ancien, elle ne veut plus rien dire pour le nouveau.
              onChange={(e) => onChange(list.map((it, i) => (i === idx ? { component_id: e.target.value, metadata: {} } : it)))}
            >
              {GRAPH_COMPONENTS.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
            {compInfo?.metadataField === "selected_metrics" && (
              <TagInput
                tags={item.metadata?.selected_metrics || []}
                onChange={(v) => setItem({ metadata: { ...item.metadata, selected_metrics: v } })}
                suggestions={scoreOptions.map((o) => o.value)}
              />
            )}
            {compInfo?.metadataField && compInfo.metadataField !== "selected_metrics" && (
              <RefSelect
                value={item.metadata?.[compInfo.metadataField] || ""}
                onChange={(v) => setItem({ metadata: { ...item.metadata, [compInfo.metadataField]: v } })}
                options={compInfo.onlyBreakdownScores ? breakdownOptions : scoreOptions}
                placeholder={compInfo.metadataLabel}
              />
            )}
            <button className="builder-icon-btn danger" onClick={() => onChange(list.filter((_, i) => i !== idx))}>✕</button>
          </div>
        );
      })}
    </>
  );
}

/**
 * LE CONTENU D'UN TABLEAU DE BORD : le dashboard de base, puis les composants
 * qu'on lui ajoute.
 *
 * Chaque composant graphique a — ou n'a pas — un réglage, et un seul : la
 * métrique qu'il montre, ou la liste de métriques d'un radar. Le registre
 * `GRAPH_COMPONENTS` le déclare, l'écran s'y conforme, et un nouveau composant
 * n'a rien à ajouter ici.
 */
export default function GraphsInspector({ page, onPatchContent, ctx }) {
  const scoreOptions = scoreOptionsOf(ctx.scoreDic);
  const list = page.content?.list_graph || [];

  const patchList = (next) => onPatchContent({ list_graph: next });

  return (
    <>
      <div className="builder-form-grid">
        <label className="builder-label">
          <span className="field-label-row">
            Type de dashboard
            <InfoTooltip text="'both' : CBS + Water Footprint. 'cbs' : uniquement le CBS. 'water-footprint' : uniquement l'empreinte eau." />
          </span>
          <select
            className="builder-input"
            value={page.content?.dashboard_type || "both"}
            onChange={(e) => onPatchContent({ dashboard_type: e.target.value })}
          >
            <option value="both">Les deux (CBS + Water Footprint)</option>
            <option value="cbs">CBS uniquement</option>
            <option value="water-footprint">Water Footprint uniquement</option>
          </select>
        </label>
        <label className="builder-label">
          <span className="field-label-row">
            Graphe additionnel
            <InfoTooltip text="Score_id d'un score waterfootprint complexe à décomposer (ex: chantier-waterfootprint). Optionnel." />
          </span>
          <RefSelect
            value={page.content?.additional_graph || ""}
            onChange={(v) => onPatchContent({ additional_graph: v })}
            options={scoreOptions}
            placeholder="Optionnel — choisir un score complexe…"
          />
        </label>
      </div>

      <div style={{ marginTop: 20 }}>
        <GraphListEditor list={list} onChange={patchList} scoreDic={ctx.scoreDic} />
      </div>
    </>
  );
}

/**
 * LES SCORES DU DOUBLE ANNEAU. Même exigence que le donut de commande &
 * contrôle : sans `metadata.standard` et `metadata.current`, il n'y a rien à
 * comparer — la liste ne propose donc que les scores qui en ont.
 */
export function DoughnutScoresInspector({ page, onPatchContent, ctx }) {
  const options = scoreOptionsOf(ctx.scoreDic)
    .filter((o) => {
      const s = ctx.scoreDic?.[o.value];
      return s?.metadata?.standard && s?.metadata?.current;
    });
  const list = page.content?.list_scores || [];

  return (
    <>
      <div className="builder-section-header">
        <h3 className="builder-subsection-title">
          <span className="field-label-row">
            Scores affichés en anneau ({list.length})
            <InfoTooltip text="Ces scores doivent avoir une structure metadata {standard, current} avec des sous-catégories : c'est ce que l'anneau compare." />
          </span>
        </h3>
        <button className="builder-btn-secondary" onClick={() => onPatchContent({ list_scores: [...list, ""] })}>
          + Ajouter
        </button>
      </div>

      {list.length === 0 && <div className="builder-empty">Aucun score affiché.</div>}

      {list.map((scoreId, idx) => (
        <div key={idx} className="builder-consequence-row">
          <RefSelect
            value={scoreId}
            onChange={(v) => onPatchContent({ list_scores: list.map((s, i) => (i === idx ? v : s)) })}
            options={options}
            placeholder="Choisir un score (avec metadata standard/current)…"
          />
          <button
            className="builder-icon-btn danger"
            onClick={() => onPatchContent({ list_scores: list.filter((_, i) => i !== idx) })}
          >✕</button>
        </div>
      ))}

      {options.length === 0 && (
        <div className="builder-warning-inline">
          ⚠ Aucun score de cet atelier n'a la structure metadata {"{standard, current}"} attendue par ce type de page.
        </div>
      )}
    </>
  );
}
